/**
 * Statsig Feature Flag Adapter
 *
 * This file contains ALL Statsig-specific code.
 * To switch to another provider (LaunchDarkly, etc.), replace ONLY this file.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StatsigClient } from '@statsig/js-client';
import { getAppEnvironment } from '@/config/env-config';
import type { FeatureFlag } from '@/enums/feature-flag';
import { getStatsigClientKey } from '@/providers/adapters/statsig-config';
import type { FeatureFlagContextValue } from '@/types/context/feature-flag-context.types';

interface StatsigAdapterProps {
  children: (value: FeatureFlagContextValue) => React.ReactNode;
  userId?: string;
  tenantSlug?: string | null;
}

/**
 * Statsig adapter that initializes the SDK and provides feature flag evaluation.
 * Uses render props pattern to pass the context value to the parent provider.
 *
 * Lifecycle: one StatsigClient per mount. User/tenant changes route through
 * updateUserAsync on the existing instance (Statsig best practice — avoids
 * cache-key collisions and lost analytics on re-instantiation).
 */
export function StatsigAdapter({
  children,
  userId,
  tenantSlug,
}: StatsigAdapterProps) {
  const clientRef = useRef<StatsigClient | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [evalRevision, setEvalRevision] = useState(0);

  useEffect(() => {
    const client = new StatsigClient(
      getStatsigClientKey(),
      {
        userID: userId,
        customIDs: { tenant_slug: tenantSlug ?? '' },
      },
      {
        environment: { tier: getAppEnvironment() },
      }
    );
    clientRef.current = client;
    let cancelled = false;

    client
      .initializeAsync()
      .then(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      })
      .catch(error => {
        if (!cancelled) {
          console.error('[FeatureFlags] Statsig initialization failed:', error);
        }
      });

    return (): void => {
      cancelled = true;
      client.shutdown();
      clientRef.current = null;
    };
    // Construct ONCE per mount; subsequent user/tenant changes use updateUserAsync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const client = clientRef.current;

    if (!isReady || !client) {
      return;
    }

    let cancelled = false;

    client
      .updateUserAsync({
        userID: userId,
        customIDs: { tenant_slug: tenantSlug ?? '' },
      })
      .then(() => {
        if (!cancelled) {
          setEvalRevision(revision => revision + 1);
        }
      })
      .catch(error => {
        console.error('[FeatureFlags] Statsig updateUserAsync failed:', error);
      });

    return (): void => {
      cancelled = true;
    };
  }, [userId, tenantSlug, isReady]);

  const value: FeatureFlagContextValue = useMemo(
    () => ({
      isFeatureEnabled: (flag: FeatureFlag): boolean => {
        if (!isReady || !clientRef.current) {
          return false;
        }

        try {
          return clientRef.current.checkGate(flag);
        } catch (error) {
          console.error(`[FeatureFlags] Error checking gate "${flag}":`, error);

          return false;
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evalRevision invalidates context after user update
    [isReady, evalRevision]
  );

  // The rule flags the render-prop call site because isFeatureEnabled reads
  // clientRef.current; the ref is a stable per-mount client, so this is safe.
  // eslint-disable-next-line react-hooks/refs
  return <>{children(value)}</>;
}
